import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Input, Radio, Space, Spin, Table, Typography, message } from 'antd'
import axios from 'axios'

export interface HierarchyRow {
  key: number
  value: string
  show_ms_files: boolean
}

interface SaveResponse {
  ok: boolean
  errors?: string
}

interface OpenHierarchyProps {
  orgaId: string
}

interface HierarchyFormValues {
  ranks: {
    value: string
    show_ms_files: boolean
  }[]
}

export function OpenHierarchy({ orgaId }: OpenHierarchyProps) {
  const [form] = Form.useForm<HierarchyFormValues>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const loadHierarchy = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get<HierarchyRow[]>(
        `/organisations/${orgaId}/collaborators/hierarchy/list`,
      )
      form.setFieldsValue({
        ranks: data.map(row => ({
          value: row.value,
          show_ms_files: row.show_ms_files,
        })),
      })
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger le périmètre de responsabilité')
    } finally {
      setLoading(false)
    }
  }, [form, orgaId])

  useEffect(() => {
    loadHierarchy()
  }, [loadHierarchy])

  const onFinish = async (values: HierarchyFormValues) => {
    setSaving(true)
    try {
      const payload: HierarchyRow[] = values.ranks.map((rank, index) => ({
        key: index + 1,
        value: rank.value || '',
        show_ms_files: !!rank.show_ms_files,
      }))
      const { data } = await axios.post<SaveResponse>(
        `/organisations/${orgaId}/collaborators/hierarchy`,
        payload,
        { validateStatus: status => status < 500 },
      )

      if (data.ok) {
        message.success('Périmètre de responsabilité enregistré')
        return
      }

      message.error(data.errors || "Erreur lors de l'enregistrement")
    } catch (error) {
      console.error(error)
      message.error("Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ marginTop: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ margin: '16px' }}>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Table
          pagination={false}
          rowKey={(_, index) => String((index ?? 0) + 1)}
          dataSource={[0, 1, 2, 3]}
          columns={[
            {
              title: 'Rang',
              key: 'rank',
              width: 80,
              render: (_, __, index) => index + 1,
            },
            {
              title: 'Nom',
              key: 'value',
              render: (_, __, index) => (
                <Form.Item
                  name={['ranks', index, 'value']}
                  style={{ marginBottom: 0 }}
                  className="no-box-shadow"
                >
                  <Input placeholder={`Nom du rang ${index + 1}`} />
                </Form.Item>
              ),
            },
            {
              title: 'Téléchargement fichiers MS',
              key: 'show_ms_files',
              width: 280,
              render: (_, __, index) => (
                <Form.Item
                  name={['ranks', index, 'show_ms_files']}
                  style={{ marginBottom: 0 }}
                  className="no-box-shadow"
                >
                  <Radio.Group
                    options={[
                      { label: 'Oui', value: true },
                      { label: 'Non', value: false },
                    ]}
                  />
                </Form.Item>
              ),
            },
          ]}
        />

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit" loading={saving}>
            Enregistrer
          </Button>
        </Space>
      </Form>
    </div>
  )
}
